/**
 * Copyright (c) 2023, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from "@mui/material";
import { PropTypes } from "prop-types";
import React, { useState } from "react";
import SubGroupAccordion from "./SubGroupAccordion";
import StatusBadge from "../StatusBadge";

/**
 * Monitor group accordion component
 * Description: This component is used to display the monitor groups in the accordion
 */
function MonitorGroupAccordion({ group, subgroup, availabilityHistory, defaultExpanded = true }) {
  // Groups start expanded by default and can still be collapsed by the user
  const [ expanded, setExpanded ] = useState(defaultExpanded && subgroup.length > 0);

  return (
    <Accordion
      expanded={ expanded }
      onChange={ () => setExpanded(subgroup.length === 0 ? false : !expanded) }
      disableGutters={ true }
      square={ false }
      variant="outlined"
      sx={ { p: 0.5 } }
    >
      { /* Render and display the monitor groups */ }
      <AccordionSummary>
        <Grid container>
          <Grid item xs sm md lg xl>
            <Typography variant="body1">
              { group.display_name }
            </Typography>
          </Grid>
          <Grid item xs sm md lg xl>
            <Typography variant="body1" sx={ { pr: 2, textAlign: "right" } }>
              <StatusBadge status={ Math.max(...subgroup.map((sgroup) => sgroup.status)) } />
            </Typography>
          </Grid>
        </Grid>
      </AccordionSummary>

      { /* Render the sub-group monitor details */ }
      <AccordionDetails>
        { subgroup.map((sgroup) => (
          <SubGroupAccordion
            key={ sgroup.display_name }
            name={ sgroup.display_name }
            description={ sgroup.description }
            status={ sgroup.status }
            availability={ sgroup?.availability }
            availabilityHistory={
              availabilityHistory.length > 0 &&
              availabilityHistory.find(item => item.display_name == sgroup.display_name)?.history || []
            }
          />
        )) }
      </AccordionDetails>
    </Accordion>
  );
}

MonitorGroupAccordion.propTypes = {
  availabilityHistory: PropTypes.array,
  defaultExpanded: PropTypes.bool,
  group: PropTypes.object,
  subgroup: PropTypes.arrayOf(PropTypes.object)
};

export default MonitorGroupAccordion;
